// src/App.js
import React, { useEffect, useState } from 'react';
import { db } from './firebaseConfig';
import { doc, getDoc } from 'firebase/firestore';
import { Routes, Route, Navigate } from 'react-router-dom';

import DashboardLayout from './DashboardLayout';

import Leads from './pages/Leads';
import NewLead from './pages/NewLead';
import AssignLead from './pages/AssignLead';
import EditCustomer from './pages/EditCustomer';
import LeadDocs from './pages/LeadDocs';
import LeadQuotes from './pages/LeadQuotes';

// Reports (nested canonical)
import ReportsHub from './pages/reports/ReportsHub';
import SoldLeads from './pages/reports/SoldLeads';
import ReportsPM from './pages/reports/ReportsPM';
import ReportsCancelled from './pages/reports/ReportsCancelled';
import ReportsCallbacks from './pages/reports/ReportsCallbacks';
import ReportsArchive from './pages/reports/ReportsArchive';
import SalesReportPage from './pages/reports/SalesReportPage'; // ← NEW

import Settings from './pages/Settings';
import Salespeople from './pages/Salespeople';
import DevFixClaims from './pages/DevFixClaims';
import AssignRoles from './pages/settings/AssignRoles';
import Templates from './pages/settings/Templates';

// ✅ Standalone pages
import Branding from './pages/settings/Branding';
import PriceList from './pages/settings/PriceList';

// Public pages
import Signup from './pages/Signup';
import Login from './pages/Login';

// NEW: Auth helpers (public)
import ForgotPassword from './pages/auth/ForgotPassword';
import ResetPassword from './pages/auth/ResetPassword';

// Finish-setup interstitial
import FinishSetup from './pages/FinishSetup';

// Hooks
import useOrgClaims from './hooks/useOrgClaims';
import useOrgSettings from './hooks/useOrgSettings';

// Doors Admin page (new)
import DoorsEditorPage from './pages/DoorsEditorPage';

// Terms page (public)
import TermsPage from './pages/TermsPage';

function App() {
  const { user, orgId, role, loading: claimsLoading } = useOrgClaims();
  const { companyName } = useOrgSettings();
  const [isAdmin, setIsAdmin] = useState(null);

  useEffect(() => {
    document.title = companyName ? `${companyName} — Fixihub Admin` : 'Fixihub Admin';
  }, [companyName]);

  useEffect(() => {
    let cancelled = false;
    const check = async () => {
      if (claimsLoading) return;

      // If signed in but no orgId claim yet, defer to /finish-setup.
      if (user && !orgId) {
        setIsAdmin(false);
        return;
      }

      if (!user) {
        setIsAdmin(false);
        return;
      }

      try {
        // Allow admin OR superadmin from claims
        if (role === 'admin' || role === 'superadmin') {
          setIsAdmin(true);
          return;
        }

        // Legacy fallbacks while migrating
        const orgAdminRef = doc(db, 'organizations', orgId, 'admins', user.uid);
        const orgAdminSnap = await getDoc(orgAdminRef);
        if (cancelled) return;
        if (orgAdminSnap.exists()) {
          setIsAdmin(true);
          return;
        }

        const rootAdminRef = doc(db, 'admins', user.uid);
        const rootAdminSnap = await getDoc(rootAdminRef);
        if (cancelled) return;
        setIsAdmin(rootAdminSnap.exists());
      } catch (e) {
        console.error('Admin check error:', e);
        if (!cancelled) setIsAdmin(false);
      }
    };
    check();
    return () => {
      cancelled = true;
    };
  }, [user, orgId, role, claimsLoading]);

  // Signed-in, claims loaded, but no orgId → force /finish-setup
  if (user && !claimsLoading && !orgId) {
    return (
      <Routes>
        <Route path="/finish-setup" element={<FinishSetup />} />
        {/* Public auth pages */}
        <Route path="/login" element={<Login />} />
        <Route path="/signup" element={<Signup />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        {/* Public Terms */}
        <Route path="/terms" element={<TermsPage />} />
        <Route path="*" element={<Navigate to="/finish-setup" replace />} />
      </Routes>
    );
  }

  // Not admin (or still checking) states:
  if (user && isAdmin === false) {
    return (
      <Routes>
        {/* Public auth pages */}
        <Route path="/signup" element={<Signup />} />
        <Route path="/login" element={<Login />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        {/* Keep finish-setup routable here too (in case gate flips late) */}
        <Route path="/finish-setup" element={<FinishSetup />} />
        {/* Public Terms */}
        <Route path="/terms" element={<TermsPage />} />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    );
  }

  // Not signed in yet or admin still unknown → only public routes
  if (!user || isAdmin === null) {
    return (
      <Routes>
        {/* Public auth pages */}
        <Route path="/login" element={<Login />} />
        <Route path="/signup" element={<Signup />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        {/* Also allow finish-setup to render in this state (harmless) */}
        <Route path="/finish-setup" element={<FinishSetup />} />
        {/* Public Terms */}
        <Route path="/terms" element={<TermsPage />} />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    );
  }

  // Authenticated + admin → normal app under the dashboard shell
  return (
    <DashboardLayout companyName={companyName}>
      <Routes>
        <Route path="/" element={<Navigate to="/leads" replace />} />
        <Route path="/dev/fix-claims" element={<DevFixClaims />} />

        {/* Leads */}
        <Route path="/leads" element={<Leads />} />
        <Route path="/leads/new" element={<NewLead />} />
        <Route path="/leads/:id/assign" element={<AssignLead />} />
        {/* Back-compat alias: keep both URLs working */}
        <Route path="/leads/:id/edit" element={<EditCustomer />} />
        <Route path="/leads/:id/edit-customer" element={<EditCustomer />} />
        <Route path="/leads/:id/docs" element={<LeadDocs />} />
        <Route path="/leads/:id/quotes" element={<LeadQuotes />} />

        {/* Reports — canonical nested routes */}
        <Route path="/reports" element={<ReportsHub />}>
          {/* Index should show Sales per your order */}
          <Route index element={<SoldLeads />} />
          <Route path="sales" element={<SoldLeads />} />

          {/* NEW canonical Opportunities path */}
          <Route path="opportunities" element={<ReportsPM />} />
          {/* Back-compat: old /reports/pm → /reports/opportunities */}
          <Route path="pm" element={<Navigate to="/reports/opportunities" replace />} />

          <Route path="callbacks" element={<ReportsCallbacks />} />
          <Route path="cancelled" element={<ReportsCancelled />} />
          <Route path="archive" element={<ReportsArchive />} />

          {/* NEW: standalone Sales Report page */}
          <Route path="sales-report" element={<SalesReportPage />} />

          {/* Back-compat: old /reports/hub → /reports/opportunities */}
          <Route path="hub" element={<Navigate to="/reports/opportunities" replace />} />
        </Route>

        {/* Also capture absolute /reports/hub just in case */}
        <Route path="/reports/hub" element={<Navigate to="/reports/opportunities" replace />} />

        {/* Settings sections */}
        {/* ✅ Branding uses the standalone page */}
        <Route path="/settings/branding" element={<Branding />} />
        <Route path="/settings/account" element={<Settings section="account" />} />
        <Route path="/settings/assign-roles" element={<AssignRoles />} />
        {/* ✅ Price List now uses the new standalone page */}
        <Route path="/settings/price-list" element={<PriceList />} />
        <Route path="/settings/sales-people" element={<Salespeople />} />
        <Route path="/settings/templates" element={<Templates />} />

        {/* Doors pricing editor */}
        <Route path="/pricing/doors" element={<DoorsEditorPage />} />

        {/* Public Terms (also available while signed in) */}
        <Route path="/terms" element={<TermsPage />} />

        {/* Fallback */}
        <Route path="*" element={<Navigate to="/leads" replace />} />
      </Routes>
    </DashboardLayout>
  );
}

export default App;
